import React from 'react'
import { useFormikContext } from 'formik'

import { ProfileFormValues } from '../config'
import ProfileFormWrapper from 'components/wrappers/ProfileFormWrapper'
import { ReactSelect } from 'shared'

import { getCatalogOptions } from 'utils/helpers'
import { useProfileForm } from 'utils/providers/ProfileFormProvider'
import { useAuth } from 'utils/providers/AuthProvider'
import useAttributes from 'utils/hooks/useAttributes'
import ProfileFormStepLoader from '../ProfileFormStepLoader'

const ProfileFormBeauty = () => {
  const { values, errors, setValues } = useFormikContext<ProfileFormValues>()
  const { isUserUpdating } = useProfileForm()
  const { isUserFetching } = useAuth()

  const { attributes, isLoading } = useAttributes([
    'depth',
    'undertone',
    'skin_type',
    'skin_characteristics',
    'eye_color',
    'hair_color',
    'hair_type',
    'hair_thickness',
    'hair_characteristics',
  ])

  const {
    depth,
    undertone,
    skin_type,
    skin_characteristics,
    eye_color,
    hair_color,
    hair_type,
    hair_thickness,
    hair_characteristics,
  } = attributes

  if (isUserFetching || isUserUpdating) {
    return <ProfileFormStepLoader />
  }

  return (
    <ProfileFormWrapper
      title="Beauty preferences"
      stepDescription="If you are interested in creating beauty video reviews, tell us about your attributes for all relevant categories."
    >
      <ReactSelect
        name="depth"
        options={getCatalogOptions(depth?.options)}
        value={values.getValueByAttribute(
          'depth',
          'CHARACTER',
          values.attributes
        )}
        label="Skin Tone"
        placeholder="Select skin tone"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = depth?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'depth',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.depth || ''}
      />
      <ReactSelect
        name="undertone"
        options={getCatalogOptions(undertone?.options)}
        value={values.getValueByAttribute(
          'undertone',
          'CHARACTER',
          values.attributes
        )}
        label="Undertone"
        placeholder="Select undertone"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = undertone?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'undertone',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.undertone || ''}
      />
      <ReactSelect
        name="skin_type"
        options={getCatalogOptions(skin_type?.options)}
        value={values.getValueByAttribute(
          'skin_type',
          'CHARACTER',
          values.attributes
        )}
        label="Skin type"
        placeholder="Select skin type"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = skin_type?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'skin_type',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.skin_type || ''}
      />
      <ReactSelect
        isMulti
        name="skin_characteristics"
        options={getCatalogOptions(skin_characteristics?.options)}
        value={values.getValuesByAttribute(
          'skin_characteristics',
          'CHARACTER',
          values.attributes
        )}
        label="Skin Characteristics (select all that apply)"
        placeholder="Select Skin characteristics"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItems = skin_characteristics?.options?.filter((item) =>
            e.find((el) => el.value === item.code)
          )
          setValues(
            values.setAttributeValue({
              attribute: 'skin_characteristics',
              category: 'CHARACTER',
              value: catalogItems || null,
            })
          )
        }}
        error={errors?.validationErrors?.skin_characteristics || ''}
      />
      <ReactSelect
        name="eye_color"
        options={getCatalogOptions(eye_color?.options)}
        value={values.getValueByAttribute(
          'eye_color',
          'CHARACTER',
          values.attributes
        )}
        label="Eye Color"
        placeholder="Select eye color"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = eye_color?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'eye_color',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.eye_color || ''}
      />
      <ReactSelect
        name="hair_color"
        options={getCatalogOptions(hair_color?.options)}
        value={values.getValueByAttribute(
          'hair_color',
          'CHARACTER',
          values.attributes
        )}
        label="Hair Color"
        placeholder="Select hair color"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = hair_color?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'hair_color',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.hair_color || ''}
      />
      <ReactSelect
        name="hair_type"
        options={getCatalogOptions(hair_type?.options)}
        value={values.getValueByAttribute(
          'hair_type',
          'CHARACTER',
          values.attributes
        )}
        label="Hair Type"
        placeholder="Select hair type"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = hair_type?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'hair_type',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.hair_type || ''}
      />
      <ReactSelect
        name="hair_thickness"
        options={getCatalogOptions(hair_thickness?.options)}
        value={values.getValueByAttribute(
          'hair_thickness',
          'CHARACTER',
          values.attributes
        )}
        label="Hair thickness"
        placeholder="Select hair thickness"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = hair_thickness?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'hair_thickness',
              category: 'CHARACTER',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.hair_thickness || ''}
      />
      <ReactSelect
        isMulti
        name="hair_characteristics"
        options={getCatalogOptions(hair_characteristics?.options)}
        value={values.getValuesByAttribute(
          'hair_characteristics',
          'CHARACTER',
          values.attributes
        )}
        label="Hair Characteristics (select all that apply)"
        placeholder="Select hair characteristics"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItems = hair_characteristics?.options?.filter((item) =>
            e.find((el) => el.value === item.code)
          )
          setValues(
            values.setAttributeValue({
              attribute: 'hair_characteristics',
              category: 'CHARACTER',
              value: catalogItems || null,
            })
          )
        }}
        error={errors?.validationErrors?.hair_characteristics || ''}
      />
    </ProfileFormWrapper>
  )
}

export default ProfileFormBeauty
